import { useI18n } from '@affine/i18n';
import { Heading1Icon } from '@docnboard/icons/blocksuite/rc';
import {
  ActivityIcon,
  CheckLargeIcon,
  ChevronRightIcon,
  CloseLargeIcon,
  CornerDownLeftIcon,
  CornerUpLeftIcon,
  DocumentTextIcon,
  InformationStrokeIcon,
  InternetStrokeIcon,
  ListMinusIcon,
  ListPlusIcon,
  SortDownIcon,
  SortUpIcon,
  WandIcon,
} from '@docnboard/icons/rc';
import { type SlateEditor } from '@udecode/plate';
import {
  type PlateEditor,
  useEditorRef,
  usePluginOption,
} from '@udecode/plate/react';
import { useIsSelecting } from '@udecode/plate-selection/react';
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type AIChatPluginType } from '../editor/plugins/ai-plugin/types';
import { CommandGroup, CommandItem, CommandShortcut } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type EditorChatState =
  | 'cursorCommand'
  | 'cursorSuggestion'
  | 'selectionCommand'
  | 'selectionSuggestion';

export type AiChatItem = {
  icon?: ReactNode;
  label: string;
  value: string;
  component?: ComponentType<{ menuState: EditorChatState }>;
  filterItems?: boolean;
  shortcut?: string;
  onSelect?: ({
    editor,
    aiEditor,
    plugin,
  }: {
    editor: PlateEditor;
    aiEditor: SlateEditor;
    plugin: AIChatPluginType;
  }) => void;
  items?: { value: string }[];
};

const useAIChatItems = () => {
  const { t } = useI18n();

  const chatItems: Record<string, AiChatItem> = useMemo(
    () => ({
      accept: {
        icon: <CheckLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.accept'),
        value: 'accept',
        onSelect: ({ editor, aiEditor, plugin }) => {
          editor.getApi(plugin).aiChat.actions?.('insertBelow', aiEditor);
        },
      },
      discard: {
        icon: <CloseLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.discard'),
        value: 'discard',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.hide();
          editor.getApi(plugin).aiChat.actions?.('discard');
        },
      },
      explain: {
        icon: <InformationStrokeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.explain'),
        value: 'explain',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.explain',
                },
              },
            },
          });
        },
      },
      fixGrammarAndSpelling: {
        icon: <CheckLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.fix-grammer-spelling'),
        value: 'fixGrammarAndSpelling',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.fixGrammarAndSpelling',
                },
              },
            },
          });
        },
      },
      fixSpelling: {
        icon: <CheckLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.fix-spelling'),
        value: 'fixSpelling',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.fixSpelling',
                },
              },
            },
          });
        },
      },
      fixGrammar: {
        icon: <CheckLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.fix-grammar'),
        value: 'fixGrammar',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.fixGrammar',
                },
              },
            },
          });
        },
      },
      improveWriting: {
        icon: <WandIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.improve-writing'),
        value: 'improveWriting',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.improve',
                },
              },
            },
          });
        },
      },
      insertBelow: {
        icon: <SortDownIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.insert-below'),
        value: 'insertBelow',
        onSelect: ({ aiEditor, editor, plugin }) => {
          editor.getApi(plugin).aiChat.actions?.('insertBelow', aiEditor);
        },
      },
      insertAbove: {
        icon: <SortUpIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.insert-above'),
        value: 'insertAbove',
        onSelect: ({ aiEditor, editor, plugin }) => {
          editor.getApi(plugin).aiChat.actions?.('insertAbove', aiEditor);
        },
      },
      makeLonger: {
        icon: <ListPlusIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.make-longer'),
        value: 'makeLonger',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.makeLonger',
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
      },
      translate: {
        icon: <InternetStrokeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.translate'),
        value: 'translate',
        filterItems: false,
        items: [
          {
            value: 'translateEnglish',
          },
          {
            value: 'translateRussian',
          },
          {
            value: 'translateGerman',
          },
          {
            value: 'translateItalian',
          },
          {
            value: 'translateSpanish',
          },
        ],
      },
      translateEnglish: {
        label: t('docnboard.ai.menu.translate.lang.english'),
        value: 'translateEnglish',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.translate',
                args: {
                  language: 'English',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      translateRussian: {
        label: t('docnboard.ai.menu.translate.lang.russian'),
        value: 'translateRussian',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.translate',
                args: {
                  language: 'Russian',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      translateGerman: {
        label: t('docnboard.ai.menu.translate.lang.german'),
        value: 'translateGerman',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.translate',
                args: {
                  language: 'German',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      translateItalian: {
        label: t('docnboard.ai.menu.translate.lang.italian'),
        value: 'translateItalian',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.translate',
                args: {
                  language: 'Italian',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      translateSpanish: {
        label: t('docnboard.ai.menu.translate.lang.spanish'),
        value: 'translateSpanish',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.translate',
                args: {
                  language: 'Spanish',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      changeTone: {
        icon: <ActivityIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.change-tone'),
        value: 'changeTone',
        filterItems: false,
        items: [
          {
            value: 'changeToneProfessional',
          },
          {
            value: 'changeToneInformal',
          },
          {
            value: 'changeToneFriendly',
          },
          {
            value: 'changeToneCritical',
          },
          {
            value: 'changeToneHumorous',
          },
        ],
      },
      changeToneProfessional: {
        label: t('docnboard.ai.menu.change-tone.tone.professional'),
        value: 'changeToneProfessional',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.changeTone',
                args: {
                  tone: 'professional',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      changeToneInformal: {
        label: t('docnboard.ai.menu.change-tone.tone.informal'),
        value: 'changeToneInformal',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.changeTone',
                args: {
                  tone: 'informal',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      changeToneFriendly: {
        label: t('docnboard.ai.menu.change-tone.tone.friendly'),
        value: 'changeToneFriendly',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.changeTone',
                args: {
                  tone: 'friendly',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      changeToneCritical: {
        label: t('docnboard.ai.menu.change-tone.tone.critical'),
        value: 'changeToneCritical',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.changeTone',
                args: {
                  tone: 'critical',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      changeToneHumorous: {
        label: t('docnboard.ai.menu.change-tone.tone.humorous'),
        value: 'changeToneHumorous',
        onSelect: ({ editor, plugin }) => {
          const options = {
            body: {
              options: {
                command: 'doc.changeTone',
                args: {
                  tone: 'humorous',
                },
              },
            },
          };

          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options,
          });
        },
        filterItems: true,
      },
      makeShorter: {
        icon: <ListMinusIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.make-shorter'),
        value: 'makeShorter',
        onSelect: ({ editor, plugin }) => {
          void editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.makeShorter',
                },
              },
            },
          });
        },
      },
      replace: {
        icon: <CheckLargeIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.replace'),
        value: 'replace',
        onSelect: ({ aiEditor, editor, plugin }) => {
          editor.getApi(plugin).aiChat.actions?.('replaceSelection', aiEditor);
        },
      },
      summarize: {
        icon: <DocumentTextIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.summarize'),
        value: 'summarize',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.summary',
                },
              },
            },
          });
        },
      },
      summaryAsTitle: {
        icon: <Heading1Icon className="!text-primary" />,
        label: t('docnboard.ai.menu.summary-as-title'),
        value: 'summaryAsTitle',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.summaryAsTitle',
                },
              },
            },
          });
        },
      },
      articleAbout: {
        icon: <DocumentTextIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.article-about'),
        value: 'articleAbout',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.articleAbout',
                },
              },
            },
          });
        },
      },
      outlineAbout: {
        icon: <DocumentTextIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.outline-about'),
        value: 'outlineAbout',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.outlineAbout',
                },
              },
            },
          });
        },
      },
      header: {
        icon: <Heading1Icon className="!text-primary" />,
        label: t('docnboard.ai.menu.header'),
        value: 'header',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.submit({
            mode: 'chat',
            prompt: {
              default: '{editor}',
            },
            options: {
              body: {
                options: {
                  command: 'doc.header',
                },
              },
            },
          });
        },
      },
      tryAgain: {
        icon: <CornerUpLeftIcon className="!text-primary" />,
        label: t('docnboard.ai.menu.try-again'),
        value: 'tryAgain',
        onSelect: ({ editor, plugin }) => {
          editor.getApi(plugin).aiChat.reload();
        },
      },
    }),
    [t]
  );

  const stateItems: Record<
    EditorChatState,
    {
      items: (typeof chatItems)[keyof typeof chatItems][];
      heading?: string;
    }[]
  > = useMemo(() => {
    return {
      cursorCommand: [
        {
          items: [
            // chatItems.continueWrite,
            chatItems.summarize,
            chatItems.summaryAsTitle,
            chatItems.explain,
          ],
          heading: t(
            'docnboard.ai.menu.heading.generation'
          ).toLocaleUpperCase(),
        },
      ],
      cursorSuggestion: [
        {
          items: [chatItems.accept, chatItems.discard, chatItems.tryAgain],
        },
      ],
      selectionCommand: [
        {
          items: [
            chatItems.fixGrammarAndSpelling,
            chatItems.fixSpelling,
            chatItems.fixGrammar,
          ],
          heading: t('docnboard.ai.menu.heading.checking').toLocaleUpperCase(),
        },
        {
          items: [
            chatItems.translate,
            chatItems.changeTone,
            chatItems.improveWriting,
            chatItems.makeLonger,
            chatItems.makeShorter,
          ],
          heading: t('docnboard.ai.menu.heading.editing').toLocaleUpperCase(),
        },
        {
          items: [chatItems.header],
          heading: t(
            'docnboard.ai.menu.heading.generation'
          ).toLocaleUpperCase(),
        },
        {
          items: [chatItems.articleAbout, chatItems.outlineAbout],
          heading: t('docnboard.ai.menu.heading.draft').toLocaleUpperCase(),
        },
        {
          items: [
            chatItems.translateEnglish,
            chatItems.translateRussian,
            chatItems.translateGerman,
            chatItems.translateItalian,
            chatItems.translateSpanish,
          ],
          heading: t('docnboard.ai.menu.heading.translation'),
        },
        {
          items: [
            chatItems.changeToneProfessional,
            chatItems.changeToneInformal,
            chatItems.changeToneFriendly,
            chatItems.changeToneCritical,
            chatItems.changeToneHumorous,
          ],
          heading: t('docnboard.ai.menu.heading.tone'),
        },
      ],
      selectionSuggestion: [
        {
          items: [
            chatItems.replace,
            chatItems.insertAbove,
            chatItems.insertBelow,
            chatItems.discard,
            chatItems.tryAgain,
          ],
        },
      ],
    };
  }, [chatItems, t]);

  return { chatItems, stateItems };
};

export const AIMenuItems = ({
  setValue,
  plugin,
  filterValue,
}: {
  setValue: (value: string) => void;
  plugin: AIChatPluginType;
  filterValue?: string;
}) => {
  const editor = useEditorRef();
  const { messages } = usePluginOption(plugin, 'chat');
  const aiEditor = usePluginOption(plugin, 'aiEditor');
  const isSelecting = useIsSelecting();

  const { chatItems, stateItems } = useAIChatItems();

  const menuState = useMemo(() => {
    if (messages && messages.length > 0) {
      return isSelecting ? 'selectionSuggestion' : 'cursorSuggestion';
    }

    return isSelecting ? 'selectionCommand' : 'cursorCommand';
  }, [isSelecting, messages]);

  const menuGroups = useMemo(() => {
    const items = stateItems[menuState]
      .map(item => {
        return {
          ...item,
          items: item.items.filter(item => {
            if (item.filterItems === undefined) return true;
            if (!filterValue && item.filterItems !== true) return true;
            return item.filterItems && filterValue;
          }),
        };
      })
      .filter(item => item.items.length > 0);

    return items;
  }, [menuState, filterValue, stateItems]);

  useEffect(() => {
    if (menuGroups.length > 0 && menuGroups[0].items.length > 0) {
      setValue(menuGroups[0].items[0].value);
    }
  }, [menuGroups, setValue]);

  // Состояние для открытия popover по hover
  const [openPopover, setOpenPopover] = useState<string | null>(null);

  return (
    <>
      {menuGroups.map((group, index) => (
        <CommandGroup heading={group.heading} key={index}>
          {group.items.map(menuItem => {
            if (menuItem.items && Array.isArray(menuItem.items)) {
              return (
                <Popover
                  key={menuItem.value}
                  open={openPopover === menuItem.value}
                  modal={false}
                  onOpenChange={open =>
                    setOpenPopover(open ? menuItem.value : null)
                  }
                >
                  <PopoverTrigger asChild>
                    <div
                      onMouseEnter={() => setOpenPopover(menuItem.value)}
                      onMouseLeave={() => setOpenPopover(null)}
                    >
                      <CommandItem
                        className="[&_svg]:text-muted-foreground cursor-pointer"
                        value={menuItem.label}
                      >
                        {menuItem.icon}
                        <span>{menuItem.label}</span>
                        <span style={{ marginLeft: 'auto' }}>
                          <ChevronRightIcon width={20} height={20} />
                        </span>
                      </CommandItem>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    side="right"
                    sideOffset={0}
                    onMouseEnter={() => setOpenPopover(menuItem.value)}
                    onMouseLeave={() => setOpenPopover(null)}
                    style={{ minWidth: 200, padding: 0 }}
                  >
                    {menuItem.items.map(subItem => {
                      const aiItem = chatItems[subItem.value];

                      return (
                        // TODO вынести в отдельный компонент
                        <CommandItem
                          className="[&_svg]:text-muted-foreground cursor-pointer"
                          key={aiItem.value}
                          onSelect={() => {
                            if (!aiEditor) return;
                            aiItem.onSelect?.({
                              aiEditor,
                              editor: editor,
                              plugin,
                            });
                          }}
                          value={aiItem.label}
                        >
                          {aiItem.icon}
                          <span>{aiItem.label}</span>
                          <CommandShortcut className="hidden group-data-[selected=true]:inline">
                            <CornerDownLeftIcon />
                          </CommandShortcut>
                        </CommandItem>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              );
            }
            return (
              <CommandItem
                className="[&_svg]:text-muted-foreground cursor-pointer"
                key={menuItem.value}
                onSelect={() => {
                  if (!aiEditor) return;
                  menuItem.onSelect?.({
                    aiEditor,
                    editor: editor,
                    plugin,
                  });
                }}
                value={menuItem.label}
              >
                {menuItem.icon}
                <span>{menuItem.label}</span>
                <CommandShortcut className="hidden group-data-[selected=true]:inline">
                  <CornerDownLeftIcon />
                </CommandShortcut>
              </CommandItem>
            );
          })}
        </CommandGroup>
      ))}
    </>
  );
};
